import React, { useRef, useImperativeHandle, forwardRef, useCallback, useState } from "react";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Tooltip } from "antd";

const zoom_icons = [{
    "action": "pan",
    "tooltip": "Pan",
    "icon": "/img/collaborate_tool/hand_black.svg",
    "selected_icon": "/img/collaborate_tool/hand_white.svg"
},
{
    "action": "zoom_in",
    "tooltip": "Zoom In",
    "icon": "/img/collaborate_tool/zoom_in_black.svg",
    "selected_icon": "/img/collaborate_tool/zoom_in_white.svg"
},
{
    "action": "zoom_out",
    "tooltip": "Zoom Out",
    "icon": "/img/collaborate_tool/zoom_out_black.svg",
    "selected_icon": "/img/collaborate_tool/zoom_out_white.svg"
}]

const ImageZoom = forwardRef(({ 
    imageSrc, 
    className = "", 
    style = {},
    onZoomChange = () => {},
    onPanChange = () => {},
    onTransformChange = () => {},
    onImageLoad = () => {},
    onImageError = () => {},
    minScale = 1,
    maxScale = 8,
    initialScale = 1,
    wheelStep = 2.0,
    doubleClickStep = 2,
    pinchStep = 2.0,
    enableWheel = true,
    enablePinch = true,
    enableDoubleClick = true,
    limitToBounds = true,
    centerOnInit = true,
    showZoomControls = true,
}, ref) => {
    const transformRef = useRef(null);
    const zoomFunctionsRef = useRef({});
    const prevScaleRef = useRef(initialScale);
    const prevPositionXRef = useRef(0);
    const prevPositionYRef = useRef(0);
    const [zoomLevel, setZoomLevel] = useState(Math.round(initialScale * 100));
    const [selectedAction, setSelectedAction] = useState("");

    useImperativeHandle(ref, () => ({
        zoomIn: (event) => {
            if (zoomFunctionsRef.current.zoomIn) {
                zoomFunctionsRef.current.zoomIn(event);
            }
        },
        zoomOut: (event) => {
            if (zoomFunctionsRef.current.zoomOut) {
                zoomFunctionsRef.current.zoomOut(event);
            }
        },
        resetTransform: () => {
            if (zoomFunctionsRef.current.resetTransform) {
                zoomFunctionsRef.current.resetTransform();
            }
        },
        setTransform: (scale, positionX, positionY, animationTime = 0) => {
            if (zoomFunctionsRef.current.setTransform) {
                zoomFunctionsRef.current.setTransform(positionX, positionY, scale, animationTime);
            }
        },
        zoomToScale: (scale, animationTime = 0) => {
            if (zoomFunctionsRef.current.zoomToElement) {
                zoomFunctionsRef.current.zoomToElement('center', scale, animationTime);
            }
        },
        getTransform: () => {
            return {
                scale: prevScaleRef.current,
                positionX: prevPositionXRef.current,
                positionY: prevPositionYRef.current
            };
        },
    }));

    const handleTransformChange = useCallback((scale, positionX, positionY) => {
        setZoomLevel(Math.round(scale * 100));
        onZoomChange(scale);
        onPanChange({ x: positionX, y: positionY });
        onTransformChange({ scale, positionX, positionY });
    }, [onZoomChange, onPanChange, onTransformChange]);

    // Throttle function to prevent too frequent updates during animations
    const throttledHandleTransformChange = useCallback((() => {
        let timeoutId;
        return (scale, positionX, positionY) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                handleTransformChange(scale, positionX, positionY);
            }, 16); // ~60fps throttling
        };
    })(), [handleTransformChange]);

    const handleSelectAction = (action) => {
        if (selectedAction === action) {
            setSelectedAction("");
        } else {
            setSelectedAction(action);
        }
    };

    return (
        <TransformWrapper
            ref={transformRef}
            defaultScale={initialScale}
            minScale={minScale}
            maxScale={maxScale}
            centerZoomedOut={true}
            wheel={{ step: wheelStep }}
            doubleClick={{ step: doubleClickStep }}
            pinch={{ step: pinchStep }}
            centerOnInit={centerOnInit}
            limitToBounds={limitToBounds}
            initialPositionX={0}
            initialPositionY={0}
            options={{
                limitToBounds,
                transformEnabled: true,
                disabled: false,
                enableWheel,
                enablePinch,
                enableDoubleClick,
                velocityEqualToMove: true,
                enableVelocity: true,
                limitsOnWheel: false
            }}
        >
            {({ zoomIn, zoomOut, resetTransform, scale, positionX, positionY, ...rest }) => {
                zoomFunctionsRef.current = { zoomIn, zoomOut, resetTransform, ...rest };
                
                if (scale !== prevScaleRef.current || 
                    positionX !== prevPositionXRef.current || 
                    positionY !== prevPositionYRef.current) {
                    
                    throttledHandleTransformChange(scale, positionX, positionY);
                    
                    prevScaleRef.current = scale;
                    prevPositionXRef.current = positionX;
                    prevPositionYRef.current = positionY;
                }
                
                return (
                    <div style={{cursor: selectedAction === "pan" ? 'grab' : 'default'}}>
                        <TransformComponent>
                            <img 
                                src={imageSrc}
                                className={`ai-image-zoom-img ${className}`}
                                style={{
                                   
                                    objectFit: 'contain',
                                    objectPosition: 'center',
                                    userSelect: 'none',
                                    ...style
                                }}
                                alt="Zoomable Image"
                                draggable={false}
                                onLoad={(e) => {
                                    const img = e.target;
                                    if (img.naturalWidth && img.naturalHeight) {
                                        onImageLoad({
                                            width: img.naturalWidth,
                                            height: img.naturalHeight
                                        });
                                    }
                                }}
                                onError={onImageError}
                            />
                        </TransformComponent>
                        
                        {showZoomControls && (
                            <div className='pos-ai-zoom-container'>
                                <div className='ai-zoom-container'>
                                    {zoom_icons.map((icon, index) => (
                                        <Tooltip
                                            key={index}
                                            title={<span className='manrope f-12 white'>
                                                {icon.tooltip}
                                            </span>}>
                                            <div className={`tool-box justify-in-center ${selectedAction == icon.action ? `product-qa-selected` : ``}`}
                                                onClick={(event) => {
                                                    if (icon.action === "zoom_in") {
                                                        zoomIn(event);
                                                    } else if (icon.action === "zoom_out") {
                                                        zoomOut(event);
                                                    } else {
                                                        handleSelectAction(icon.action);
                                                    }
                                                }}>
                                                <img src={selectedAction == icon.action ? icon.selected_icon : icon.icon} className='zoom-tool-icons' />
                                            </div>
                                        </Tooltip>
                                        ))}
                                    <div className='manrope f-12 w-700 lh-20 black-085'>
                                        {zoomLevel}%
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                );
            }}
        </TransformWrapper>
    );
});

ImageZoom.displayName = 'ImageZoom';

export default ImageZoom;
